import Box from '@mui/material/Box';
import { DataGrid, GridActionsCellItem, GridColDef, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarFilterButton } from '@mui/x-data-grid';
import { FC, useEffect, useState } from 'react';
import { ReEvent, Id, RolesForEvent, ReSession, SessionStatus, UserDetails } from '../../interfaces/types';
import { fetchUsers } from '../../services/firebaseService';
import { ChangeCircle, Edit, Group, Reviews, Visibility } from '@mui/icons-material';
import { useCustomThemeContext } from '../../providers/customThemeProvider';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useEventContext } from '../../providers/EventProvider';
import { Button, Typography } from '@mui/material';
import { usePermissionContext } from '../../providers/PermissionProvider';
import { useSessionContext } from '../../providers/SessionProvider';
import SessionBulkEditDialog from './SessionBulkEditDialog';
import { useUserContext } from '../../providers/userProvider';
import { useReviewContext } from '../../providers/ReviewProvider';


interface GridInputProps {
    sessions?: ReSession[];
    userHasEditPermission?: boolean;
}

const SessionOverviewGrid: FC<GridInputProps> = ({ sessions = [], userHasEditPermission = false }) => {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const { theme } = useCustomThemeContext();
    const { getEventById } = useEventContext();
    const { updateEventRoleOnUsers } = useUserContext();
    const { getNumberOfReviewsForSession } = useReviewContext();
    const [users, setUsers] = useState<UserDetails[]>([]);
    const [columns, setColumns] = useState<GridColDef[]>([]);
    const [rows, setRows] = useState<ReSession[]>([]);
    const [event, setEvent] = useState<ReEvent>();
    const { hasSessionEditPermission, hasManagerOrHigherPermission } = usePermissionContext();
    const { getSessionById, updateSession, hasUserAtLeastOneSessionConfirmed } = useSessionContext();
    const [selectedRows, setSelectedRows] = useState<Id[]>([]);
    const [openBulkEditDialog, setOpenBulkEditDialog] = useState<boolean>(false);



    useEffect(() => {
        fetchUsers()
            .then((fetchedUsers) => {
                if (fetchedUsers) {
                    setUsers(fetchedUsers);
                }
            });

        if (eventId) {
            const fetchedEvent = getEventById(eventId);
            setEvent(fetchedEvent);
        }
    }, [eventId, getEventById]);

    useEffect(() => {
        // Update the users' roles for the event
        const usersToCheck = Array.from(new Set(sessions.map(session => session.presenterIds).flat()));

        //console.log("checked:", usersToCheck);
        const usersToUpdateToPresenter = users.filter(user => usersToCheck.includes(user.uid) && hasUserAtLeastOneSessionConfirmed(user.uid));
        const usersToUpdateToRevokePresenter = users.filter(user => usersToCheck.includes(user.uid) && !hasUserAtLeastOneSessionConfirmed(user.uid));
        {/* console.log("Add role to:", usersToUpdateToPresenter);
        console.log("Revoke:", usersToUpdateToRevokePresenter);
        console.log("event id:", eventId);*/}


        (async () => {
            try {
                await updateEventRoleOnUsers(eventId as Id, RolesForEvent.PRESENTER, usersToUpdateToRevokePresenter, usersToUpdateToPresenter);
                const fetchedUsers = await fetchUsers();
                if (fetchedUsers) {
                    setUsers(fetchedUsers);
                }
            } catch (error) {
                console.error("Error updating users' roles or fetching users:", error);
            }
        })();

        setRows(sessions);
    }, [sessions]);

    useEffect(() => {
        const newColumns: GridColDef[] = [
            {
                field: 'topic',
                headerAlign: 'center', // aligns the header
                align: 'center', //aligns the text in the column
                headerName: 'Topic'.toUpperCase(),
                width: 200,
                type: 'singleSelect',
                valueOptions: event?.eventConfiguration?.topics.map((topic) => ({
                    value: topic.id,
                    label: topic.name,
                })),
                renderCell: (params) => {
                    const topic = params.row.topic;
                    const eventTopic = event?.eventConfiguration?.topics.find((eventTopic) => eventTopic.id === topic as Id);

                    if (!topic || !eventTopic) {
                        return null;
                    }
                    return (
                        <Box
                            sx={{
                                backgroundColor: eventTopic.color,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center', // Aligns the content horizontally
                            }}
                        >
                            <Typography variant="body2" sx={{
                                alignContent: 'center', // Aligns the content vertically
                                textAlign: 'center', // Ensures text is centered
                                whiteSpace: 'normal', // Allows text to wrap
                                wordWrap: 'break-word', // Breaks long words
                                paddingLeft: 1, paddingRight: 1,
                            }}>
                                {eventTopic.name}
                            </Typography>
                        </Box>
                    );
                },
            },
            {
                field: 'title',
                headerName: 'Title'.toUpperCase(),
                width: 500, // Adjusted width to show 70 chars
            },
            {
                field: 'subtitle',
                headerName: 'Subtitle'.toUpperCase(),
                width: 600, // Adjusted width to show 70 chars
            },
            {
                field: 'maxParticipants',
                headerAlign: 'center', // aligns the header
                align: 'center', //aligns the text in the column
                headerName: 'max Participants'.toUpperCase(),
                renderHeader: () => <Group />,
                width: 100,
                type: 'number',
            },
            {
                field: 'id', //field id is nessecary for the valueGetter
                headerAlign: 'center', // aligns the header
                align: 'center', //aligns the text in the column
                headerName: 'Reviews'.toUpperCase(),
                description: 'Number of reviews on the session',
                renderHeader: () => <Reviews />,
                width: 75,
                type: 'number',
                valueGetter: (id) => {
                    return getNumberOfReviewsForSession(getSessionById(id as Id) as ReSession);
                }
            },
            {
                field: 'presenterIds',
                headerName: 'Presented by'.toUpperCase(),
                width: 200,
                valueGetter: (value) => {
                    const values = value as string[];
                    return values.map((presenterId: string) => {
                        const user = users.find((user) => user.uid === presenterId);
                        return user ? `${user.firstName} ${user.lastName}` : '';
                    }).join(', ');
                },
            },
            {
                field: 'status',
                headerAlign: 'center', // aligns the header
                align: 'center', //aligns the text in the column
                headerName: 'Status'.toUpperCase(),
                width: 150,
                type: 'singleSelect',
                valueOptions: Object.values(SessionStatus).map((status) => ({
                    value: status,
                    label: status,
                })),
            },
            {
                field: 'createdAt',
                headerAlign: 'center', // aligns the header
                align: 'center', //aligns the text in the column
                headerName: 'Created at'.toUpperCase(),
                width: 150,
                valueGetter: (value) => {
                    const date = dayjs(value as unknown as string);
                    return date.format('DD/MM/YYYY, HH:mm');
                },

            },
            {
                field: 'updatedAt',
                headerAlign: 'center', // aligns the header
                align: 'center', //aligns the text in the column
                headerName: 'Last updated at'.toUpperCase(),
                width: 150,
                valueGetter: (value) => {
                    const date = dayjs(value as unknown as string);
                    return date.format('DD/MM/YYYY, HH:mm');
                },
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions'.toUpperCase(),
                width: 100,
                cellClassName: 'actions',

                getActions: ({ id }) => {
                    if (hasSessionEditPermission(event?.id as Id, getSessionById(id as Id) as ReSession)) {
                        return [
                            <GridActionsCellItem
                                icon={<Visibility />}
                                label="View"
                                onClick={() => navigate(`/session/view/${id}`)}
                                color="inherit"
                            />,
                            <GridActionsCellItem
                                icon={<Edit />}
                                label="Edit"
                                onClick={() => navigate(`/session/edit/${id}`)}
                                color="inherit"
                            />,
                        ];
                    }
                    return [
                        <GridActionsCellItem
                            icon={<Visibility />}
                            label="View"
                            onClick={() => navigate(`/session/${id}`)}
                            color="inherit"
                        />,
                    ];
                },
            },
        ];

        if (!hasManagerOrHigherPermission(event?.id as Id)) {
            const filteredColumns = newColumns.filter(column =>
                !['Status', 'Reviews', 'Last updated at', 'Created at', 'max Participants' ].map(column => column.toLowerCase()).includes((column.headerName ?? '').toLowerCase()));
            setColumns(filteredColumns);
        } else {
            setColumns(newColumns);
        }
    }, [userHasEditPermission, users]);

    const handleSessionBulkEditOpen = () => {
        setOpenBulkEditDialog(true);
    };

    const handleSessionBulkEditClose = async (status: string) => {
        setOpenBulkEditDialog(false);

        // Update the status of the selected sessions
        const updatedSessionsPromises = sessions.map(async (session) => {
            if (selectedRows.includes(session.id)) {
                const updatedSession = { ...session, status: Object.values(SessionStatus).find((value) => value === status) as SessionStatus };
                await updateSession(updatedSession);

                //console.log("session updated");
                return updatedSession;
            }
            return session;
        });

        // Wait for all updateSession calls to complete
        const updatedSessions = await Promise.all(updatedSessionsPromises);

        // Update the rows state to reflect the changes in the DataGrid
        setRows(updatedSessions);


        //setUsers(usersToUpdate);
        setSelectedRows([]);
    };

    return (
        <Box sx={{ height: "80vh", width: '100%' }}>
            <DataGrid
                sx={{
                    border: 2,
                    borderColor: theme.palette.primary.main,
                }}
                rows={rows}
                getRowHeight={() => 'auto'}
                columns={columns}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 5,
                        },
                    },
                    columns: {
                        columnVisibilityModel: {
                            // Hide columns, the other columns will remain visible
                            subtitle: false,
                            createdAt: false,
                            updatedAt: false,
                            maxParticipants: false,
                            id: false,
                        },
                    },
                }}
                disableColumnMenu={true}
                slots={{
                    toolbar: () => (
                        <GridToolbarContainer sx={{ mt: 1 }}>
                            <GridToolbarFilterButton />
                            <GridToolbarDensitySelector />
                            <GridToolbarColumnsButton />
                            {userHasEditPermission && (
                                <Button onClick={handleSessionBulkEditOpen}
                                    disabled={selectedRows.length === 0}>
                                    <ChangeCircle />
                                    <Typography fontSize={13} fontWeight={500}>Change status</Typography>
                                </Button>
                            )}
                            <SessionBulkEditDialog open={openBulkEditDialog} selectedValue={''} onClose={handleSessionBulkEditClose} />

                        </GridToolbarContainer>
                    ),
                }}
                pageSizeOptions={[5]}
                paginationMode='server' //set to server to get rid of the warning
                rowCount={rows.length} //added to get rid of th</>e warning

            checkboxSelection = {userHasEditPermission}
            disableRowSelectionOnClick
            editMode="row"
            onRowSelectionModelChange={(newSelection) => {
                setSelectedRows(newSelection as Id[]);
            }}
            rowSelectionModel={selectedRows}
        />
    </Box>
);

};

export default SessionOverviewGrid;